import { Tag, Tooltip } from 'antd';
import { FC } from 'react';

import { Schemas } from '@api-client/generated/types';
import { formatNumber } from '@constants';
import { formatReferenceDay } from '@utils';

type Reconciliation = Schemas.ReconciliationResult;
type NotReconcilableReason = NonNullable<Reconciliation['reason']>;

type AccountReconciliationProps = {
  reconciliation?: Reconciliation;
  currencyCode: string;
};

/**
 * Why an account could not be checked, in words rather than in enum values.
 *
 * `no_booked_balance` is not an edge case: it is roughly seven in ten of our connected accounts,
 * because Revolut and Wise together are two thirds of them and neither publishes a booked balance.
 * The wording has to leave a reader in no doubt that nothing was verified — "no mismatch found"
 * would be read as "checked and fine".
 */
const REASONS: Record<NotReconcilableReason, string> = {
  no_booked_balance:
    'The bank publishes only an available balance for this account. An available balance nets card holds, which are not booked entries, so it cannot be compared against the transactions.',
  reference_date_unchanged:
    'The bank has not moved the date on its balance, so there is no period to attribute transactions to.',
  balance_type_changed:
    'The bank changed which balance it publishes during the period, so the two ends measure different things.',
  balance_fetch_failed: 'The bank refused the last balance requests.',
  no_second_observation:
    'Only one balance observation so far. A comparison needs two, so this account becomes checkable after the next sync.',
  no_observations:
    'No balance observations recorded yet. They start accumulating from the account’s first sync.',
};

const formatMoney = (amount: number, currencyCode: string) =>
  formatNumber(amount, { style: 'currency', currency: currencyCode });

/**
 * Whether the account's balance moved by exactly the transactions we hold for it.
 *
 * A mismatch means one of two things, and the tooltip gives a reader enough to tell them apart: a
 * transaction the bank booked never reached us, or we hold one it does not. The figures are the
 * whole argument, so they are all there rather than just the difference.
 *
 * Every figure is optional in the response — only `accountId` and `status` are guaranteed — so none
 * of them is defaulted here. A figure that did not arrive is left out, and a mismatch whose amount
 * is unknown still shows as a mismatch: inventing `Off by €0.00` would be misleading, but hiding the
 * mismatch behind a dash would be worse.
 */
const AccountReconciliation: FC<AccountReconciliationProps> = ({
  reconciliation,
  currencyCode,
}) => {
  // No reconciliation at all: a CSV-only account, whose balance the import maintains itself.
  if (!reconciliation) {
    return <>—</>;
  }

  if (reconciliation.status === 'not_reconcilable') {
    return (
      <Tooltip
        title={
          reconciliation.reason
            ? REASONS[reconciliation.reason]
            : 'This account could not be checked.'
        }
      >
        <Tag>Not checked</Tag>
      </Tooltip>
    );
  }

  const { window, balanceType, balanceDelta, transactionsTotal, difference } =
    reconciliation;

  const rows = [
    window && (
      <div key="window">
        {formatReferenceDay(window.from)} → {formatReferenceDay(window.to)}
        {balanceType ? ` · ${balanceType}` : ''}
      </div>
    ),
    balanceDelta !== undefined && (
      <div key="delta">Balance moved {formatMoney(balanceDelta, currencyCode)}</div>
    ),
    transactionsTotal !== undefined && (
      <div key="total">
        Transactions total {formatMoney(transactionsTotal, currencyCode)}
      </div>
    ),
    reconciliation.status === 'mismatch' && difference !== undefined && (
      <div key="difference">
        Unexplained {formatMoney(difference, currencyCode)}
      </div>
    ),
  ].filter(Boolean);

  // No figures at all means no argument to show; the verdict still stands on its own.
  const detail = rows.length > 0 ? <>{rows}</> : undefined;

  if (reconciliation.status === 'matched') {
    return (
      <Tooltip title={detail}>
        <Tag color="green">Matches</Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={detail}>
      <Tag color="red">
        {difference === undefined
          ? 'Mismatch'
          : `Off by ${formatMoney(Math.abs(difference), currencyCode)}`}
      </Tag>
    </Tooltip>
  );
};

export default AccountReconciliation;
